import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import CustomCheckbox from '../customCheckbox';
import UserAvatar from '../userAvatar';
import ConditionalTooltip from '../conditionalTooltip';
import DeletedUserAvatar from '../deletedUserAvatar';
import ActionButton from '../actionButton';
import { getPersonFullName } from '../../../utility/uiUtils';
import { getTaskContextLinkProps } from '../../../utility/tasks';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    borderRadius: 16,
    display: 'grid',
    gridTemplateColumns: '1fr 24px 28px',
    gridColumnGap: 6,
    alignItems: 'center',
    padding: spacing(4),
    width: '100%',
    minHeight: 64,
    userSelect: 'none',
  },
  taskWrapper: {
    width: '100%',
    flexShrink: 1,
  },
  userWrapper: {
    boxSizing: 'border-box',
    paddingTop: spacing(0.5),
    height: '100%',
  },
  tooltipWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing(0.5),
  },
  user: {
    transition: 'box-shadow .25s ease-in',
    '&:hover': {
      boxShadow: `0 0 0 1px ${primary.white}, 0 0 0 2px ${primary.blue1}`,
    },
  },
  linkWrapper: {
    boxSizing: 'border-box',
    paddingTop: spacing(1),
    height: '100%',
  },
  linkButton: {
    width: 24,
    height: 24,
  },
  buttonIcon: {
    width: 14,
    height: 7,
  },
}));

const TaskCard = ({
  className = undefined,
  translations,
  currentUser,
  task = {},
  onChange,
}) => {
  const classes = useStyles();
  const { text, is_finished, creator, context } = task;
  const isCurrentUserCreator = currentUser?.id === creator?.id;
  const contextLinkProps = getTaskContextLinkProps(context);

  const tooltipText = `${
    isCurrentUserCreator
      ? `${translations.assignedBy} ${translations.you}`
      : translations.assignedBy
  } ${isCurrentUserCreator ? '' : getPersonFullName(creator)}`;

  const handleChange = useCallback(() => {
    return onChange(task);
  }, [task, onChange]);

  return (
    <div className={classNames(classes.root, className)}>
      <CustomCheckbox
        className={classes.taskWrapper}
        isChecked={is_finished}
        labelText={text}
        onChange={handleChange}
        shouldTopAlign
        isControlled
        highlightedText
        smallText
      />
      <div className={classes.linkWrapper}>
        <ActionButton
          iconButtonClass={classes.linkButton}
          iconClass={classes.buttonIcon}
          type={ACTION_BUTTON_TYPES.LINK}
          tooltipText={translations.linkButtonTooltip.text}
          disabledTooltipText={translations.linkButtonTooltip.disabled}
          isDisabled={!context.allowed}
          linkProps={{
            ...contextLinkProps,
          }}
          isLink={context.allowed}
        />
      </div>
      {creator?.id ? (
        <div className={classes.userWrapper}>
          <ConditionalTooltip
            className={classes.tooltipWrapper}
            message={tooltipText}
            addTooltip
          >
            <UserAvatar customAvatarClass={classes.user} user={creator} small />
          </ConditionalTooltip>
        </div>
      ) : (
        <DeletedUserAvatar
          tooltipText={translations.deletedUserTooltip}
          isSmall
        />
      )}
    </div>
  );
};

TaskCard.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.object.isRequired,
  task: PropTypes.object,
  currentUser: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default TaskCard;
